import * as DateFnsNs from 'date-fns'
import lodash from 'lodash'
import { type MRT_ColumnSizingState } from 'material-react-table'
import ConfirmExportDataLimitPresenter from 'src/main/ConfirmExportDataLimit.presenter'
import { excludedExportDocIds } from 'src/main/Main.scopes'
import * as lang from 'src/utils/lang'
import { Logger, Presenter } from 'wdc-cube'
import FilterManager from './tcm_filter_manager'
import { TheConsumerMarketPresenter } from './tcm_presenter'
import { TableQSAScope } from './tcm_scopes'
import {
    JunctionMode,
    StringKeywordOperator,
    TheConsumerMarketService,
    type ColumnFilter,
    type ColumnSort,
    type EmpresaFilter
} from './tcm_service'
import { type TCMTexts } from './tcm_texts'

type PaginationState = {
    pageIndex: number
    pageSize: number
}

const LOG = Logger.get('TcmPresenterForQSAListagem')

const service = TheConsumerMarketService.singleton()

export default class TcmPresenterForQSAListagem extends Presenter<TableQSAScope, TheConsumerMarketPresenter> {
    // :: Class methods

    constructor(owner: TheConsumerMarketPresenter) {
        super(owner, owner.scope.listagemTabs.qsaListagem)
        this.__parent = owner
        this.__texts = owner.texts
        this.__filterManager = owner.filterManager
        this.__confirmExportDataPresenter = new ConfirmExportDataLimitPresenter(
            owner.app,
            this.scope.exportDataLimitScope
        )
    }

    // :: Instance

    private readonly __parent: TheConsumerMarketPresenter
    private readonly __texts: TCMTexts
    private readonly __filterManager: FilterManager
    private readonly __confirmExportDataPresenter: ConfirmExportDataLimitPresenter

    // :: Public API

    async initialize() {
        this.scope.update = this.update
        this.scope.caption = this.__texts.qsaListagem
        this.scope.onSelectionChange = this.action(this.__onSocioRowClicked)
        this.scope.onFiltrationChange = this.action(this.__onFiltrationChange)
        this.scope.onPaginationChange = this.action(this.__onPaginationChange)
        this.scope.onSortingChange = this.action(this.__onSortingChange)
        this.scope.onDownloadClick = this.action(this.__onDownloadClicked)
        this.scope.setColumnSizing = this.action(this.__onSetColumnSizing)
        LOG.debug('ready')

        this.scope.exportDataLimitScope.onYesNoClicked = this.action(this.__onYesNoConfirmExport)
        this.scope.exportDataLimitScope.canExcludeAlreadyExported = true

        this.__confirmExportDataPresenter.initialize()
    }

    override release(): void {
        this.__confirmExportDataPresenter.release()
        super.release()
    }

    clear() {
        this.scope.pageIndex = 0
        this.scope.pageSize = 100
        this.owner.filterPane.socioNome.clear()
        this.__filterManager.socioKey.clear()
        this.__filterManager.socioNome.clear()
    }

    async fetch(
        filter: EmpresaFilter,
        cfg?: {
            paginationState?: PaginationState
            sorting?: ColumnSort[]
            columnFilters?: ColumnFilter[]
        }
    ) {
        let { paginationState, sorting, columnFilters } = cfg ?? {}
        this.scope.loaded = false
        try {
            if (!columnFilters) {
                columnFilters = this.scope.filters
            }

            if (!paginationState) {
                paginationState = {
                    pageIndex: 0,
                    pageSize: this.scope.pageSize
                }
            }

            if (!sorting) {
                sorting = this.scope.sorting
            }

            let search_after: unknown
            if (paginationState.pageIndex > 0) {
                const lastRow = this.scope.dataset[this.scope.dataset.length - 1]
                if (lastRow) {
                    const sortValues: unknown[] = []
                    for (const sortItem of sorting) {
                        sortValues.push((lastRow as Record<string, unknown>)[sortItem.id])
                    }
                    sortValues.push(lastRow.socio_id)
                    search_after = sortValues
                }
            }

            const resp = await service.fetch({
                listagem_qsa: {
                    sorting,
                    columnFilters,
                    search_after,
                    pageSize: paginationState.pageSize
                },
                filter: filter
            })

            this.scope.pageIndex = paginationState.pageIndex
            this.scope.pageSize = paginationState.pageSize
            this.scope.sorting = sorting

            const dataset = resp.listagem_qsa ?? []
            for (let i = 0; i < dataset.length; i++) {
                const row = dataset[i]
                if (lodash.isString(row.entrada_sociedade_dta)) {
                    row.entrada_sociedade_dta = DateFnsNs.parseISO(row.entrada_sociedade_dta)
                }
            }
            LOG.debug(`qsa_dataset.length=${dataset.length}`)

            this.scope.rowCount = resp.listagem_qsa_agg ? resp.listagem_qsa_agg['qsa-count'].value | 0 : 0
            this.scope.dataset = dataset
            this.scope.datasetVersion++

            this.__markSelected(undefined)
        } finally {
            this.scope.loaded = true
        }
    }

    private async __onSocioRowClicked(selectionMap: Map<string, boolean>) {
        const socioKeyMap = new Map<string, boolean>()

        this.scope.dataset.forEach((row) => {
            if (selectionMap.get(row.key) === true) {
                socioKeyMap.set(row.key, true)
            }
        })

        const prop = this.__filterManager.socioKey
        prop.clear()
        if (socioKeyMap.size > 0) {
            prop.add({
                mode: JunctionMode.AND,
                operator: StringKeywordOperator.is_in,
                value: [...socioKeyMap.keys()].join(', ')
            })
        }

        this.__markSelected(socioKeyMap)
        this.__parent.fetchData(socioKeyMap.size > 0 ? 'listagemDeQSA' : '')
    }

    private __markSelected(map: Map<string, boolean> | undefined) {
        if (!map) {
            map = this.collectExplicitValues()
        }

        for (const row of this.scope.dataset) {
            row.selected = map.has(row.key)
        }

        this.scope.update()
    }

    async __onFiltrationChange(columnFilters: ColumnFilter[]) {
        this.scope.filters = columnFilters
        await this.fetch(this.__filterManager.build(), {
            columnFilters
        })
    }

    async __onPaginationChange(pageIndex: number, pageSize: number) {
        if (pageSize !== this.scope.pageSize) {
            pageIndex = 0
        }
        await this.fetch(this.__filterManager.build(), {
            paginationState: { pageIndex, pageSize }
        })
    }

    async __onSortingChange(sorting: ColumnSort[]) {
        await this.fetch(this.__filterManager.build(), { sorting })
    }

    async executeDownload() {
        try {
            this.scope.working = true
            this.scope.exportDataLimitScope.isDownloading = true

            const columns = this.scope.exportColumns

            const blob = await service.downloadXlsx(
                this.__filterManager.build(),
                columns,
                'socios',
                this.scope.exportDataLimitScope.qttExportRecords,
                this.__parent.app.compliance,
                excludedExportDocIds(this.scope.exportDataLimitScope)
            )

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([blob]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `tcm-socio.xlsx`)

            // Append to html link element page
            document.body.appendChild(link)
            try {
                // Start download
                link.click()
            } finally {
                // Clean up and remove the link
                link.parentNode?.removeChild(link)
            }
        } finally {
            this.scope.exportDataLimitScope.isDownloading = false
            this.scope.working = false
        }
    }

    async __onDownloadClicked(columns: string[]) {
        this.scope.exportColumns = columns

        this.scope.exportDataLimitScope.isChecking = true
        this.scope.update()
        try {
            const limitExportData = await service.checkLimitExportData(this.__filterManager.build(), 'socios')
            await this.scope.exportDataLimitScope.onCheckLimitExportData(limitExportData)
        } finally {
            this.scope.exportDataLimitScope.isChecking = false
        }
    }

    async __onSetColumnSizing(columns: MRT_ColumnSizingState) {
        this.scope.columnSizing = columns
    }

    protected async __onYesNoConfirmExport(yes: boolean) {
        LOG.debug(`__onYesNoConfirmExport: yes=${yes}`)
        if (yes) {
            await this.executeDownload()
        }
        this.scope.exportDataLimitScope.open = false
        this.scope.update()
    }

    private collectExplicitValues() {
        const keyValMap = new Map<string, boolean>()

        const prop = this.__filterManager.socioKey

        for (const filterVal of prop.values()) {
            if (filterVal.value) {
                if (filterVal.operator == StringKeywordOperator.is_in) {
                    lang.splitValues(filterVal.value).forEach((key) => key && keyValMap.set(key, true))
                    continue
                }

                if (filterVal.operator == StringKeywordOperator.is_not_in) {
                    lang.splitValues(filterVal.value).forEach((key) => keyValMap.delete(key))
                    continue
                }

                if (filterVal.operator == StringKeywordOperator.is) {
                    const key = lodash.trim(filterVal.value.trim())
                    key && keyValMap.set(key, true)
                    continue
                }

                if (filterVal.operator == StringKeywordOperator.is_not) {
                    keyValMap.delete(filterVal.value)
                    continue
                }
            }
        }
        return keyValMap
    }
}
